import React from "react";

import cx from "classnames";

import { useOutsideClick } from "../../helpers/useOutsideClick";
import { IconButton } from "../IconButton";
import { Input } from "../Input";

import styles from "./SearchWithTypeAhead.scss";

export const defaultI18n = {
  search: "Search for",
  clear: "Clear",
};

const defaultInputProps = () => ({
  helperText: "",
  error: "",
  type: "text",
  maxLength: undefined,
  placeholder: "Search",
  required: false,
});

/**
 * @typedef {object} SearchI18n
 * @param {string} [search="Search"]
 * @param {string} [clear="Clear"]
 */

/**
 * @typedef {object} SearchWithTypeAheadProps
 * @prop {string} id The ID of the input box
 * @prop {string} [menuId] The ID of the menu list
 * @prop {string} value Text to render in the input box
 * @prop {Function} onSetOpen A function that is invoked when a user action prompts hiding the type ahead results. Takes a single argument,a boolean that if false means that the user wants to close the menu.
 * @prop {Boolean} [menuOpen=false] Whether to show the menu drop down
 * @prop {Function} onChange The function to handle changes to the text input box
 * @prop {React.ReactElement[]} [typeAheadResults=[]] An array of React elements to render under the search box
 * @prop {any} [inputProps={}] Props to pass through to the input element. Do not pass onChange here. We will ignore it.
 * @prop {string} [wrapperClassName=""]
 * @prop {boolean} [fullWidth=false]
 * @prop {string} [resultsWrapperClassName=""]
 * @prop {boolean} [showSearchIcon=true]
 * @prop {Function} [onClear] If set, we add a "clear" button to the left of the search icon. When that button is activated, we call this function
 * @prop {Function} onSetOpen If the user performs some action that should open or close the input, we call this function. Signature: `(open: boolean) => void`
 * @prop {Function} [onSubmit] If set, we add a magnifying glass (search) icon. When the user clicks that or presses enter in the search box, we invoke this function. Signature: `(query: string) => void`
 * @prop {SearchI18n} [i18n]
 */

/**
 * A controlled search box with type-ahead completion
 * @param {SearchWithTypeAheadProps} props
 */
export default function SearchWithTypeAhead({
  id = "searchWithTypeAhead",
  menuId = "listItemsMenu",
  menuOpen = false,
  value = "",
  wrapperClassName = "",
  resultsWrapperClassName = "",
  onChange,
  typeAheadResults = [],
  fullWidth = false,
  inputProps = {},
  onClear,
  onSubmit,
  onSetOpen = () => null,
  i18n = {},
}) {
  const containerRef = React.useRef();
  const i18nValues = Object.assign({}, defaultI18n, i18n);

  const mergedInputProps = Object.assign(defaultInputProps(), inputProps);
  const isMenuOpen = menuOpen && typeAheadResults.length > 0;

  useOutsideClick({
    elementRefs: [containerRef],
    handler: () => {
      if (menuOpen) {
        onSetOpen(false);
      }
    },
    dependencies: [menuOpen],
    ignoreEffect: !menuOpen,
  });

  return (
    <div
      ref={containerRef}
      className={cx(styles.searchWithTypeAhead, wrapperClassName, {
        [styles.fullWidth]: fullWidth,
      })}
    >
      <div className={styles["button-wrapper"]}>
        {onClear && value.length > 0 && (
          <div className={styles.clearIcon}>
            <IconButton
              icon="close"
              onClick={onClear}
              title={i18nValues.clear}
              iconSize={16}
            />
          </div>
        )}
        {onSubmit && (
          <div className={styles.searchIcon}>
            <IconButton
              icon="search"
              onClick={() => onSubmit(value)}
              title={i18nValues.search}
              iconSize={16}
            />
          </div>
        )}
      </div>
      <Input
        {...mergedInputProps}
        className={cx(mergedInputProps.className, {
          [styles.singleButton]:
            !(onSubmit && onClear) && (onSubmit || onClear),
          [styles.doubleButton]: onSubmit && onClear,
        })}
        onChange={(newValue) => onChange(newValue)}
        value={value}
        fullWidth={true}
        id={id}
        onFocus={() => onSetOpen(true)}
        autoComplete="off"
      />
      <div
        id={menuId}
        className={cx(styles.resultsWrapper, resultsWrapperClassName, {
          [styles.resultsWrapperVisible]: isMenuOpen,
        })}
      >
        {typeAheadResults}
      </div>
    </div>
  );
}
